import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { popoverStyle, useFloatingPopover } from "./useFloatingPopover";
import "./AutoSourcing.css";

const MENU_WIDTH = 224;

/**
 * Compact Liquid Glass action menu anchored to a trigger element. Rendered in
 * a portal with fixed positioning so scroll containers never clip it; it
 * flips above the trigger when there is not enough room below (shared
 * useFloatingPopover) and becomes a bottom sheet on small screens (CSS).
 *
 * items: [{ key, label, icon?, onSelect, danger?, separatorBefore?, disabled? }]
 */
export default function GlassMenu({ anchorRef, open, onClose, items, label, testId = "glass-menu" }) {
  const menuRef = useRef(null);
  const [active, setActive] = useState(0);
  const enabled = items.filter((i) => !i.disabled);

  const pos = useFloatingPopover({
    open,
    anchorRef,
    floatingRef: menuRef,
    width: MENU_WIDTH,
    align: "end",
  });

  useEffect(() => {
    if (!open) return undefined;
    setActive(0);
    const t = setTimeout(() => menuRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])')?.focus(), 0);
    const onPointer = (e) => {
      if (menuRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose({ focusAnchor: false });
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose({ focusAnchor: true });
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const focusItem = (index) => {
    const nodes = menuRef.current?.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])');
    if (!nodes?.length) return;
    const next = (index + nodes.length) % nodes.length;
    setActive(next);
    nodes[next].focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(active + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(active - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusItem(0); }
    else if (e.key === "End") { e.preventDefault(); focusItem(enabled.length - 1); }
    else if (e.key === "Tab") { onClose({ focusAnchor: false }); }
  };


  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={label}
      className={`as-glass-menu as-popover as-action-menu placement-${pos?.placement || "bottom"}`}
      style={popoverStyle(pos)}
      data-placement={pos?.placement || "bottom"}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      {items.map((item) => (
        <React.Fragment key={item.key}>
          {item.separatorBefore && <div className="as-glass-separator" role="separator" />}
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            aria-disabled={item.disabled ? true : undefined}
            className={`as-action-item ${item.danger ? "danger" : ""}`}
            onClick={() => {
              if (item.disabled) return;
              onClose({ focusAnchor: false });
              item.onSelect();
            }}
            data-testid={`${testId}-${item.key}`}
          >
            {item.icon ? <item.icon className="as-action-icon" aria-hidden="true" /> : null}
            <span>{item.label}</span>
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body,
  );
}
